<template>
    <div id="scroll-container" ref="fullpage">
        <stage-main v-if="addHomeStage" ref="stageMain" />
        <dialogue-slider ref="dialogueSlider" />
        <dialogue-outro />
        <experience-principles-opener />
        <eP-section v-bind="epSections.fastAndEasy" ref="ep-fast-and-easy" />
        <eP-section v-bind="epSections.secureAndAppreciated" ref="ep-secure-and-appreciated" />
        <eP-section v-bind="epSections.joyAndTogetherness" ref="ep-joy-and-togetherness" />
        <making-our-brand-tangible-slider ref="brandSlider" />
        <cta-brand-design />

        <site-footer />
    </div>
</template>

<script>
    import StageMain from './sections/StageMain.vue';
    import DialogueSlider from './sections/dialogue/DialogueSlider.vue';
    import DialogueOutro from './sections/dialogue/DialogueOutro.vue';
    import ExperiencePrinciplesOpener from './sections/experience-principles/ExperiencePrinciplesOpener.vue';
    import EPSection from './sections/experience-principles/EPSection.vue';
    import MakingOurBrandTangibleSlider from './sections/making-our-brand-tangible/MakingOurBrandTangibleSlider.vue';
    import CtaBrandDesign from './sections/CtaBrandDesign';
    import SiteFooter from './sections/SiteFooter.vue';
    import fullpage from 'fullpage.js';

    export default {
        name: 'ScrollContainer',
        components: {
            StageMain,
            DialogueSlider,
            DialogueOutro,
            ExperiencePrinciplesOpener,
            EPSection,
            MakingOurBrandTangibleSlider,
            CtaBrandDesign,
            SiteFooter,
        },
        data() {
            return {
                addBackgroundShape: true,
                addHomeStage: true,
                fullpageApi: undefined,
                epSections: {
                    fastAndEasy: {
                        headline: 'Fast and easy',
                        copy:
                            'Make it simple and provide me with clarity and guidance. Consider my context and let me focus on the things that really matter to me so that I feel supported in every interaction.',
                        overlayCopy:
                            'The focus is on content and function. Minimalistic and clear branding provides immediate orientation and recognition.',
                        buttonIcon: 'plus',
                        visual: 'phone',
                        shapeId: 'fastAndEasy',
                        sectionName: 'ep-fast-and-easy',
                        lottieData: 'ep-fast-and-easy',
                        particles: [
                            {
                                shape: 'parallelogram',
                                color: 'geyser',
                                top: 50,
                                left: 2,
                            },
                            {
                                shape: 'circle',
                                color: 'wild-blue-yonder',
                                top: 70,
                                size: 'l',
                                left: 8,
                            },
                        ],
                    },
                    secureAndAppreciated: {
                        headline: 'Secure and appreciated',
                        copy:
                            'Act as you said and make transparent what is going on. I expect you to keep the promises you made. Treat me fair at all times and make me feel welcome and taken care of.',
                        overlayCopy:
                            'We use our brand elements to create a secure and familiar environment. During crucial moments, such as rewarding a customer, our brand plays a more visible and bonding role.',
                        title: 'joyAndTogetherness',
                        id: 'joy-and-togetherness',
                        sectionName: 'ep-secure-and-appreciated',
                        shapeId: 'secureAndAppreciated',
                        buttonIcon: 'plus',
                        visual: 'speaker',
                        lottieData: 'ep-secure-and-appreciated',
                        lottieColorChange: true,
                        particles: [
                            {
                                shape: 'circle',
                                color: 'geyser',
                                top: 65,
                                size: 'l',
                                left: 5,
                            },
                            {
                                shape: 'parallelogram',
                                color: 'magenta',
                                top: 72,
                                left: 7,
                            },
                        ],
                    },
                    joyAndTogetherness: {
                        headline: 'Joy and togetherness',
                        copy:
                            'Inspire me with an exciting world of opportunities. Help me to joyfully discover and try out new things. I want to feel as part of a community. Enable me to share my experiences anytime with the people I care about.',
                        overlayCopy:
                            'There are moments in a customer journey that deserve to be celebrated. We highlight these branded interactions with strong and captivating elements.',
                        title: 'joyAndTogetherness',
                        id: 'joy-and-togetherness',
                        sectionName: 'ep-joy-and-togetherness',
                        shapeId: 'joyAndTogetherness',
                        buttonIcon: 'plus',
                        visual: 'tv',
                        lottieData: 'ep-joy-and-togetherness',
                        particles: [
                            {
                                shape: 'circle',
                                color: 'magenta',
                                top: 76,
                                left: 2,
                            },
                            {
                                shape: 'parallelogram',
                                color: 'catskill-white',
                                top: 45,
                                size: 'l',
                                left: 5,
                            },
                        ],
                    },
                },
                makingOurBrandTangibleSections: {
                    visualDesign: {
                        headline: 'Visual Design',
                        copy:
                            'Our Liquid Brand Design system is built around our corporate color Magenta. It ensures strong recognition while enabling creative flexibility.',
                        vimeoId: 453641291,
                        vimeoCoverImg: 'video-cover-visual-design',
                        shapeId: 'visualDesign',
                        sectionName: 'visual-design',
                        particles: [
                            {
                                shape: 'parallelogram',
                                color: 'geyser',
                                top: 50,
                                left: 2,
                            },
                            {
                                shape: 'circle',
                                color: 'wild-blue-yonder',
                                top: 70,
                                size: 'l',
                                left: 8,
                            },
                        ],
                    },
                    audioIdentity: {
                        headline: 'Audio Identity',
                        copy:
                            'From our iconic sound logo to functional sounds to guidance for voice interactions – our holistic approach to audio branding includes everything you need to be heard.',
                        vimeoId: 465762450,
                        vimeoCoverImg: 'video-cover-audio-identity',
                        shapeId: 'audioIdentity',
                        sectionName: 'audio-identity',
                        particles: [
                            {
                                shape: 'parallelogram',
                                color: 'magenta',
                                top: 72,
                                left: 7,
                            },
                        ],
                    },
                    typeface: {
                        headline: 'Typeface',
                        copy:
                            'Our typeface »TeleNeo« was exclusively designed for Deutsche Telekom. It plays an important role in creating a distinctive and recognizable look for our brand.',
                        vimeoId: 475520510,
                        vimeoCoverImg: 'video-cover-typeface',
                        shapeId: 'typeface',
                        sectionName: 'typeface',
                        particles: [
                            {
                                shape: 'circle',
                                color: 'magenta',
                                size: 'l',
                                top: 2,
                                right: 5,
                            },
                            {
                                shape: 'parallelogram',
                                color: 'catskill-white',
                                top: 76,
                                size: 'l',
                                left: 5,
                            },
                        ],
                    },
                },
            };
        },
        mounted() {
            if (!this.fullpageApi) {
                this.fullpageApi = new fullpage('#scroll-container', {
                    css3: true,
                    normalScrollElements: '.cookie-configurator',
                    licenseKey: '51B3EB74-5D6F4BD6-B8C432FF-FCCF3AE7',
                    anchors: [
                        'opener',
                        'user-and-brand',
                        'user-and-brand-outro',
                        'experience-principles',
                        'ep-fast-and-easy',
                        'ep-secure-and-appreciated',
                        'ep-joy-and-togetherness',
                        'making-our-brand-tangible',
                        'cta-brand-and-design',
                        'footer',
                    ],
                    onLeave: (origin, destination, direction) => {
                        origin.item.classList.add('on-leave');

                        // do all of the necessary resets
                        setTimeout(() => {
                            origin.item.classList.remove('on-leave');

                            if (origin.anchor === 'opener') {
                                this.$refs.stageMain.resetSlider();
                            }

                            if (origin.anchor === 'user-and-brand') {
                                this.$refs.dialogueSlider.resetSlider();
                            }

                            if (origin.anchor === 'making-our-brand-tangible') {
                                this.$refs.brandSlider.resetSlider();
                            }
                            /* Calling slider reset every time to workaround flickity bug. */
                            //this.$refs.brandSlider.resetSlider();

                            if (origin.anchor.startsWith('ep-')) {
                                this.$refs[origin.anchor].resetLottie();
                            }
                        }, 500);

                        this.$store.dispatch('goToSection', {
                            id: destination.index,
                            name: destination.item.dataset.anchor,
                            backgroundShape: destination.item.dataset.shapeId,
                            logoColorScheme: destination.item.dataset.logoColorScheme,
                        });
                    },
                });
            }
        },
        beforeDestroy() {
            this.fullpageApi.destroy('all');
        },
        methods: {
            lockScrolling(value) {
                this.fullpageApi.setAllowScrolling(value);
                this.fullpageApi.setKeyboardScrolling(value);
            },
        },
        computed: {
            isCookieConfiguratorModalOpen() {
                return this.$store.getters.isCookieConfiguratorModalOpen;
            },
            isVideoModalOpen() {
                return this.$store.getters.isVideoModalOpen;
            },
            isArModalOpen() {
                return this.$store.getters.isArModalOpen;
            },
        },
        watch: {
            isCookieConfiguratorModalOpen() {
                this.lockScrolling(!this.isCookieConfiguratorModalOpen);
            },
            isVideoModalOpen() {
                this.lockScrolling(!this.isVideoModalOpen);
            },
            isArModalOpen() {
                this.lockScrolling(!this.isArModalOpen);
            },
        },
    };
</script>

<style>
    /*!
 * fullPage 3.0.8
 * https://github.com/alvarotrigo/fullPage.js
 *
 * @license GPLv3 for open source use only
 * or Fullpage Commercial License for commercial use
 * http://alvarotrigo.com/fullPage/pricing/
 *
 * Copyright (C) 2018 http://alvarotrigo.com/fullPage - A project by Alvaro Trigo
 */
    html.fp-enabled,
    .fp-enabled body {
        margin: 0;
        padding: 0;
        overflow: hidden;

        /*Avoid flicker on slides transitions for mobile phones #336 */
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }
    .fp-section {
        position: relative;
        -webkit-box-sizing: border-box; /* Safari<=5 Android<=3 */
        -moz-box-sizing: border-box; /* <=28 */
        box-sizing: border-box;
    }
    .fp-slide {
        float: left;
    }
    .fp-slide,
    .fp-slidesContainer {
        height: 100%;
        display: block;
    }
    .fp-slides {
        z-index: 1;
        height: 100%;
        overflow: hidden;
        position: relative;
        -webkit-transition: all 0.3s ease-out; /* Safari<=6 Android<=4.3 */
        transition: all 0.3s ease-out;
    }
    .fp-section.fp-table,
    .fp-slide.fp-table {
        display: table;
        table-layout: fixed;
        width: 100%;
    }
    .fp-tableCell {
        display: table-cell;
        vertical-align: middle;
        width: 100%;
        height: 100%;
    }
    .fp-slidesContainer {
        float: left;
        position: relative;
    }
    .fp-controlArrow {
        -webkit-user-select: none; /* webkit (safari, chrome) browsers */
        -moz-user-select: none; /* mozilla browsers */
        -khtml-user-select: none; /* webkit (konqueror) browsers */
        -ms-user-select: none; /* IE10+ */
        position: absolute;
        z-index: 4;
        top: 50%;
        cursor: pointer;
        width: 0;
        height: 0;
        border-style: solid;
        margin-top: -38px;
        -webkit-transform: translate3d(0, 0, 0);
        -ms-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
    }
    .fp-controlArrow.fp-prev {
        left: 15px;
        width: 0;
        border-width: 38.5px 34px 38.5px 0;
        border-color: transparent #fff transparent transparent;
    }
    .fp-controlArrow.fp-next {
        right: 15px;
        border-width: 38.5px 0 38.5px 34px;
        border-color: transparent transparent transparent #fff;
    }
    .fp-scrollable {
        overflow: hidden;
        position: relative;
    }
    .fp-scroller {
        overflow: hidden;
    }
    .iScrollIndicator {
        border: 0 !important;
    }
    .fp-notransition {
        -webkit-transition: none !important;
        transition: none !important;
    }
    #fp-nav {
        position: fixed;
        z-index: 100;
        margin-top: -32px;
        top: 50%;
        opacity: 1;
        -webkit-transform: translate3d(0, 0, 0);
    }
    #fp-nav.fp-right {
        right: 17px;
    }
    #fp-nav.fp-left {
        left: 17px;
    }
    .fp-slidesNav {
        position: absolute;
        z-index: 4;
        opacity: 1;
        -webkit-transform: translate3d(0, 0, 0);
        -ms-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
        left: 0 !important;
        right: 0;
        margin: 0 auto !important;
    }
    .fp-slidesNav.fp-bottom {
        bottom: 17px;
    }
    .fp-slidesNav.fp-top {
        top: 17px;
    }
    #fp-nav ul,
    .fp-slidesNav ul {
        margin: 0;
        padding: 0;
    }
    #fp-nav ul li,
    .fp-slidesNav ul li {
        display: block;
        width: 14px;
        height: 13px;
        margin: 7px;
        position: relative;
    }
    .fp-slidesNav ul li {
        display: inline-block;
    }
    #fp-nav ul li a,
    .fp-slidesNav ul li a {
        display: block;
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        cursor: pointer;
        text-decoration: none;
    }
    #fp-nav ul li a.active span,
    .fp-slidesNav ul li a.active span,
    #fp-nav ul li:hover a.active span,
    .fp-slidesNav ul li:hover a.active span {
        height: 12px;
        width: 12px;
        margin: -6px 0 0 -6px;
        border-radius: 100%;
    }
    #fp-nav ul li a span,
    .fp-slidesNav ul li a span {
        border-radius: 50%;
        position: absolute;
        z-index: 1;
        height: 4px;
        width: 4px;
        border: 0;
        background: #333;
        left: 50%;
        top: 50%;
        margin: -2px 0 0 -2px;
        -webkit-transition: all 0.1s ease-in-out;
        -moz-transition: all 0.1s ease-in-out;
        -o-transition: all 0.1s ease-in-out;
        transition: all 0.1s ease-in-out;
    }
    #fp-nav ul li:hover a span,
    .fp-slidesNav ul li:hover a span {
        width: 10px;
        height: 10px;
        margin: -5px 0px 0px -5px;
    }
    #fp-nav ul li .fp-tooltip {
        position: absolute;
        top: -2px;
        color: #fff;
        font-size: 14px;
        font-family: arial, helvetica, sans-serif;
        white-space: nowrap;
        max-width: 220px;
        overflow: hidden;
        display: block;
        opacity: 0;
        width: 0;
        cursor: pointer;
    }
    #fp-nav ul li:hover .fp-tooltip,
    #fp-nav.fp-show-active a.active + .fp-tooltip {
        -webkit-transition: opacity 0.2s ease-in;
        transition: opacity 0.2s ease-in;
        width: auto;
        opacity: 1;
    }
    #fp-nav ul li .fp-tooltip.fp-right {
        right: 20px;
    }
    #fp-nav ul li .fp-tooltip.fp-left {
        left: 20px;
    }
    .fp-auto-height.fp-section,
    .fp-auto-height .fp-slide,
    .fp-auto-height .fp-tableCell {
        height: auto !important;
    }

    .fp-responsive .fp-auto-height-responsive.fp-section,
    .fp-responsive .fp-auto-height-responsive .fp-slide,
    .fp-responsive .fp-auto-height-responsive .fp-tableCell {
        height: auto !important;
    }

    /*Only display content to screen readers*/
    .fp-sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
</style>
